import { FormEvent, useEffect, useMemo, useState } from "react";
import Header from "../component/Header";
import Footer from "../component/footer";
import Input from "../component/Input";
import CompactDateTimePicker from "../components/CompactDateTimePicker";
import { getAuthToken } from "../utils/authSession";
import { getPlans, Plan } from "../services/planService";
import {
    createClass,
    getClasses,
    LiveClassRecord,
    updateClass,
} from "../services/classService";
import {
    formatClassDate,
    formatClassTimeOnly,
    getAdminTimezone,
    isClassPastByEnd,
    localScheduleToUtcPayload,
    utcIsoToLocalDateAndTime,
    validateScheduleInputs,
} from "../utils/classTime";
import { showErrorToast, showSuccessToast } from "../utils/toaster";

type AdminTab = "active" | "past";

interface ClassFormState {
    name: string;
    url: string;
    recordedClassLink: string;
    date: string;
    startTime: string;
    endTime: string;
    planIds: number[];
    isActive: boolean;
}

const emptyForm = (): ClassFormState => ({
    name: "",
    url: "",
    recordedClassLink: "",
    date: "",
    startTime: "",
    endTime: "",
    planIds: [],
    isActive: true,
});

const inputHeightClass = "min-h-[56px]";

const LiveClassesAdmin = () => {
    const adminTimezone = getAdminTimezone();
    const [classes, setClasses] = useState<LiveClassRecord[]>([]);
    const [plans, setPlans] = useState<Plan[]>([]);
    const [activeTab, setActiveTab] = useState<AdminTab>("active");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<ClassFormState>(emptyForm);
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        const intervalId = window.setInterval(() => setNowMs(Date.now()), 30000);
        return () => window.clearInterval(intervalId);
    }, []);

    const planMap = useMemo(() => new Map(plans.map((plan) => [plan.id, plan.name])), [plans]);

    const loadPageData = async () => {
        const token = getAuthToken();
        if (!token) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const [classesResponse, plansResponse] = await Promise.all([
                getClasses(token),
                getPlans(),
            ]);
            setClasses(classesResponse.data || []);
            setPlans((plansResponse.data || []).filter((plan) => plan.is_active));
        } catch (error) {
            showErrorToast(error instanceof Error ? error.message : "Unable to load live classes.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadPageData();
    }, []);

    const { activeClasses, pastClasses } = useMemo(() => {
        const activeItems: LiveClassRecord[] = [];
        const pastItems: LiveClassRecord[] = [];

        classes.forEach((classItem) => {
            if (isClassPastByEnd(classItem.end_date, nowMs)) {
                pastItems.push(classItem);
            } else {
                activeItems.push(classItem);
            }
        });

        activeItems.sort(
            (left, right) => new Date(left.class_date).getTime() - new Date(right.class_date).getTime(),
        );
        pastItems.sort(
            (left, right) => new Date(right.end_date).getTime() - new Date(left.end_date).getTime(),
        );

        return { activeClasses: activeItems, pastClasses: pastItems };
    }, [classes, nowMs]);

    const visibleClasses = activeTab === "active" ? activeClasses : pastClasses;

    const resetForm = () => {
        setEditingId(null);
        setForm(emptyForm());
    };

    const startEdit = (classItem: LiveClassRecord) => {
        const startParts = utcIsoToLocalDateAndTime(classItem.class_date, adminTimezone);
        const endParts = utcIsoToLocalDateAndTime(classItem.end_date, adminTimezone);

        setEditingId(classItem.id);
        setForm({
            name: classItem.name,
            url: classItem.url || "",
            recordedClassLink: classItem.recorded_class_link || "",
            date: startParts.date,
            startTime: startParts.time,
            endTime: endParts.time,
            planIds: [...classItem.plan_ids],
            isActive: classItem.is_active,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const togglePlan = (planId: number) => {
        setForm((current) => ({
            ...current,
            planIds: current.planIds.includes(planId)
                ? current.planIds.filter((id) => id !== planId)
                : [...current.planIds, planId],
        }));
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const validationError = validateScheduleInputs(
            form.name,
            form.url,
            form.date,
            form.startTime,
            form.endTime,
            form.planIds,
        );

        if (validationError) {
            showErrorToast(validationError);
            return;
        }

        const token = getAuthToken();
        if (!token) {
            showErrorToast("Your session has expired. Please login again.");
            return;
        }

        const schedule = localScheduleToUtcPayload(form.date, form.startTime, form.endTime, adminTimezone);
        const recordedClassLink = form.recordedClassLink.trim();
        const payload = {
            name: form.name.trim(),
            url: form.url.trim(),
            recorded_class_link: recordedClassLink.length > 0 ? recordedClassLink : null,
            class_date: schedule.class_date,
            end_date: schedule.end_date,
            end_time: schedule.end_time,
            timezone: adminTimezone,
            is_active: form.isActive,
            plan_ids: form.planIds,
        };

        try {
            setIsSaving(true);
            if (editingId) {
                await updateClass(token, editingId, payload);
                showSuccessToast("Class updated successfully.");
            } else {
                await createClass(token, payload);
                showSuccessToast("Class created successfully.");
            }

            resetForm();
            await loadPageData();
        } catch (error) {
            showErrorToast(error instanceof Error ? error.message : "Unable to save class.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <Header />
            <div className="min-h-screen bg-[#F4F5F7] font-sans">
                <main className="mx-auto max-w-7xl px-4 py-10 md:px-12 md:py-16 space-y-10">
                    <div>
                        <h1 className="text-2xl md:text-4xl font-extrabold text-[#113A28] tracking-tight">
                            Live Classes
                        </h1>
                        <p className="mt-2 text-sm md:text-base text-gray-500">
                            Schedule and manage live sessions for your learners.
                        </p>
                    </div>

                    <section className="bg-white rounded-[2rem] border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 md:p-10">
                        <h2 className="text-xl font-extrabold text-[#113A28] mb-6">
                            {editingId ? "Edit class" : "Add class"}
                        </h2>
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            <Input
                                id="class-name"
                                label="Class name"
                                value={form.name}
                                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                                required
                            />
                            <Input
                                id="class-url"
                                label="Live Class URL"
                                type="url"
                                value={form.url}
                                onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))}
                                required
                            />
                            <Input
                                id="recorded-class-link"
                                label="Recorded class link (optional)"
                                type="url"
                                value={form.recordedClassLink}
                                onChange={(event) =>
                                    setForm((current) => ({ ...current, recordedClassLink: event.target.value }))
                                }
                            />

                            <CompactDateTimePicker
                                dateValue={form.date}
                                startTimeValue={form.startTime}
                                endTimeValue={form.endTime}
                                onDateChange={(value) => setForm((current) => ({ ...current, date: value }))}
                                onStartTimeChange={(value) => setForm((current) => ({ ...current, startTime: value }))}
                                onEndTimeChange={(value) => setForm((current) => ({ ...current, endTime: value }))}
                                timezoneLabel={adminTimezone}
                            />

                            <div>
                                <p className="block mb-2 ml-1 text-xs font-bold tracking-wider text-gray-500 uppercase">
                                    Plans
                                    <span className="text-red-500 ml-1">*</span>
                                </p>
                                <div
                                    className={`w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl flex flex-wrap gap-3 ${inputHeightClass} items-center`}
                                >
                                    {plans.length === 0 ? (
                                        <span className="text-sm text-gray-500">No active plans available.</span>
                                    ) : (
                                        plans.map((plan) => {
                                            const checked = form.planIds.includes(plan.id);
                                            return (
                                                <label
                                                    key={plan.id}
                                                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold cursor-pointer transition-colors ${
                                                        checked
                                                            ? "border-[#4CE470] bg-[#4CE470]/10 text-[#113A28]"
                                                            : "border-gray-200 bg-white text-gray-600 hover:border-[#4CE470]/40"
                                                    }`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="h-4 w-4 rounded border-gray-300 text-[#113A28] focus:ring-[#4CE470]"
                                                        checked={checked}
                                                        onChange={() => togglePlan(plan.id)}
                                                    />
                                                    {plan.name}
                                                </label>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            <label className="inline-flex items-center gap-3 text-sm font-bold text-[#113A28]">
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded border-gray-300 text-[#113A28] focus:ring-[#4CE470]"
                                    checked={form.isActive}
                                    onChange={(event) =>
                                        setForm((current) => ({ ...current, isActive: event.target.checked }))
                                    }
                                />
                                Active / visible to learners
                            </label>

                            <div className="flex flex-col sm:flex-row gap-3 pt-2">
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="rounded-xl bg-[#4CE470] px-6 py-3 text-sm font-bold text-[#113A28] hover:bg-[#3bc45d] disabled:opacity-60"
                                >
                                    {isSaving ? "Saving..." : editingId ? "Update class" : "Add class"}
                                </button>
                                {editingId && (
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="rounded-xl border border-gray-200 px-6 py-3 text-sm font-bold text-gray-600 hover:bg-gray-50"
                                    >
                                        Cancel edit
                                    </button>
                                )}
                            </div>
                        </form>
                    </section>

                    <section className="space-y-6">
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => setActiveTab("active")}
                                className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                                    activeTab === "active"
                                        ? "bg-[#113A28] text-white"
                                        : "bg-white text-gray-600 border border-gray-200 hover:border-[#4CE470]"
                                }`}
                            >
                                Active & Upcoming ({activeClasses.length})
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab("past")}
                                className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                                    activeTab === "past"
                                        ? "bg-[#113A28] text-white"
                                        : "bg-white text-gray-600 border border-gray-200 hover:border-[#4CE470]"
                                }`}
                            >
                                Past Classes ({pastClasses.length})
                            </button>
                        </div>

                        {isLoading ? (
                            <p className="text-sm font-semibold text-gray-500 py-8 text-center">Loading classes...</p>
                        ) : visibleClasses.length === 0 ? (
                            <p className="text-sm font-semibold text-gray-500 py-8 text-center">
                                No classes in this section yet.
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                {visibleClasses.map((classItem) => {
                                    const planLabel = classItem.plan_ids
                                        .map((planId) => planMap.get(planId) || `Plan ${planId}`)
                                        .join(", ");
                                    const isPastTab = activeTab === "past";
                                    const timeClass = isPastTab ? "text-gray-500" : "text-[#113A28]";

                                    return (
                                        <article
                                            key={classItem.id}
                                            className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div>
                                                    <p className="text-xs font-bold uppercase tracking-wide text-gray-400">
                                                        {planLabel || "No plans"}
                                                    </p>
                                                    <h3 className="text-lg font-extrabold text-[#113A28] mt-1">
                                                        {classItem.name}
                                                    </h3>
                                                    <p className="text-sm font-medium text-gray-500 mt-1">
                                                        {formatClassDate(classItem.class_date)}
                                                    </p>
                                                </div>
                                                {!classItem.is_active && (
                                                    <span className="shrink-0 rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-600">
                                                        Inactive
                                                    </span>
                                                )}
                                            </div>

                                            <div className={`mt-4 space-y-1 text-sm font-semibold ${timeClass}`}>
                                                <p>Starts at {formatClassTimeOnly(classItem.class_date)}</p>
                                                <p>Ends at {formatClassTimeOnly(classItem.end_date)}</p>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => startEdit(classItem)}
                                                className="mt-5 rounded-xl border border-[#113A28] px-4 py-2.5 text-sm font-bold text-[#113A28] hover:bg-[#113A28] hover:text-white transition-colors"
                                            >
                                                Edit
                                            </button>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                </main>
            </div>
            <Footer />
        </>
    );
};

export default LiveClassesAdmin;
